// FIN-865: header-name constants and the inbound-id validator shared by every Node app that
// talks to the BFF/edge functions (apps/client, apps/mcp) — kept in one place so the header
// names and the "is this a well-formed id" check can never drift between the two.
//
// Mirrors supabase/functions/_shared/utils/requestContext.ts's own constants/regex 1:1
// (that file is Deno-only and can't be imported from Node, so this is a deliberate,
// reviewed duplication of the shape, not a second source of truth for the id itself — the
// edge function is always the one that mints/normalizes the id that ends up on any row).
export const CORRELATION_HEADER = "x-harmony-correlation-id";
export const SESSION_HEADER = "x-harmony-session-id";
// FIN-865 review round 3 — the channel a browser-side Connect call's `reference` rides on
// (ConnectError.metadata, since a plain property doesn't survive that wire hop). Shared so
// apps/client's edgeFunctionProxy.ts (writer) and packages/ui's errors.ts (reader) can't drift
// on the header name.
export const REFERENCE_HEADER = "x-harmony-reference";
// FIN-1524 — the same channel for a refusal's stable code and its sentence parameters, so a
// browser-side Connect call (where a plain property does not survive the hop) can still word
// `errors.codes.<code>` in the user's language. `apps/client`'s edgeFunctionProxy.ts writes them;
// packages/ui's errors.ts reads them.
export const ERROR_CODE_HEADER = "x-harmony-error-code";
export const ERROR_PARAMS_HEADER = "x-harmony-error-params";

// FIN-865 (Q5) — apps/client only: proxy.ts also sets this as a non-httpOnly cookie on every
// matching request, alongside the header. Invariant: this is what lets CorrelationProvider pick
// up a fresh id purely client-side on a same-layout navigation the layout server component
// itself doesn't re-run for. See specs/in-app-feedback/requirements.md.
export const CORRELATION_COOKIE = "harmony-correlation-id";

// A UUID (any version/variant) — crypto.randomUUID() always produces this shape.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Opaque client-minted token — no UUID shape required, just a safe character set so a
// malformed/hostile value can't inject arbitrary bytes into a forwarded header or a log line.
const OPAQUE_ID_RE = /^[0-9a-zA-Z_-]+$/;
const MAX_ID_LENGTH = 64;
// toReference()'s own output shape — the first 8 hex chars of a correlation id (always
// lowercase hex, since a correlation id is always a UUID per isValidCorrelationId above).
const REFERENCE_RE = /^[0-9a-f]{8}$/;

/** True only for a well-formed UUID — never trusted for anything beyond that shape check. */
export function isValidCorrelationId(id: string | null | undefined): id is string {
  return !!id && UUID_RE.test(id);
}

/**
 * True only for a well-formed 8-hex-char reference. An edge-function error body's `reference`
 * field is untrusted input like any other response field — validate its shape before it rides
 * onto a thrown error's metadata/plain property, the same way an inbound correlation id is
 * never trusted past isValidCorrelationId's shape check (EC13). Takes `unknown`, not
 * `string | null | undefined`: a JSON response body's field can be any JSON type at runtime
 * regardless of what a TS cast claims, and `RegExp.test()` silently coerces a non-string
 * argument (e.g. a number) to one — an explicit `typeof` check is what actually rejects that.
 */
export function isValidReference(ref: unknown): ref is string {
  return typeof ref === "string" && REFERENCE_RE.test(ref);
}

/** Mints a fresh correlation id — the same `crypto.randomUUID()` shape the edge functions mint. */
export function mintCorrelationId(): string {
  return crypto.randomUUID();
}

/**
 * Accepts an inbound correlation id only if well-formed, mints a fresh one otherwise (EC13:
 * "missing, malformed or oversized. A new id is minted; the inbound value is never trusted or
 * used for auth."). Mirrors requestContext.ts's normalizeCorrelationId 1:1.
 */
export function normalizeCorrelationId(raw: string | null | undefined): string {
  return isValidCorrelationId(raw) ? raw.toLowerCase() : mintCorrelationId();
}

/** Drops a malformed/oversized session id rather than substituting one — it's opaque and
 * client-minted, so there is no server-side default to fall back to. Mirrors
 * requestContext.ts's normalizeSessionId 1:1. */
export function normalizeSessionId(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_ID_LENGTH) return undefined;
  if (!OPAQUE_ID_RE.test(trimmed)) return undefined;
  return trimmed;
}

/** The short id shown to users as "Reference: <id>" — first 8 hex chars, matching the backend. */
export function toReference(correlationId: string): string {
  return correlationId.slice(0, 8);
}
